import {
  IRegion,
  ClientErrorCode,
  SessionDetails,
  IAuthRelativePath,
  IDevice,
  WsError,
  WsErrorCodes,
  WsErrorTypes,
  IAuthenticateRequest,
  IWSBrokerService,
  SessionState,
  PlatformType,
  Operation,
  ISessionManager,
  IMetrics,
  ILogger,
  generateUUID,
  WarpDriveLoginType,
  AuthProvider,
  AppConstants,
  RegionResolver,
  CryptoUtils,
} from '@amzn/work-spaces-web-client-core-lib';
import { CoreFactory } from '@/bridge/factory/CoreFactory';
import { WarpDriveExternalNativeAuthUrlBuilder } from './authUrls/WarpDriveExternalNativeAuthUrlBuilder';
import { ExternalAuthUrlBuilder } from './authUrls/ExternalAuthUrlBuilder';
import { LoginTypeDetector } from './utils/LoginTypeDetector';

interface AuthenticationProps {
  wsBrokerClient: IWSBrokerService;
  sessionManager: ISessionManager;
  metrics: IMetrics;
  logger: ILogger;
  device: IDevice;
}

const rtcchannel = CoreFactory.getRTCChannel();
export class Authentication {
  private readonly wsBrokerClient: IWSBrokerService;
  private readonly sessionManager: ISessionManager;
  private readonly metrics: IMetrics;
  private readonly logger: ILogger;
  private readonly device: IDevice;

  constructor({
    wsBrokerClient,
    sessionManager,
    metrics,
    logger,
    device,
  }: AuthenticationProps) {
    this.sessionManager = sessionManager;
    this.metrics = metrics;
    this.wsBrokerClient = wsBrokerClient;
    this.logger = logger;
    this.device = device;
  }

  /**
   * Authenticates the user with the provided authentication request
   * @param authRequest - The authentication request object
   * @param authStep - Optional authentication step path
   * @returns Authentication response
   * @throws WsError if authentication fails
   */
  async authenticate(
    authRequest: IAuthenticateRequest,
    authStep?: IAuthRelativePath
  ) {
    this.sessionManager.set({ sessionState: SessionState.AUTHENTICATE });
    const timedAuthMetric = this.metrics.embark(Operation.Authenticate);
    const regCode = this.sessionManager.get('registrationCode');
    let region = this.sessionManager.get('region');

    // Fallback: reconstruct region from regCode if missing (handles CRR codes after redirect)
    if (!region && regCode) {
      try {
        const regionResolver = CoreFactory.getRegionResolver();
        region = await regionResolver.getRegionFromRegCode(regCode);
        this.sessionManager.set({ region });
      } catch (error) {
        this.logger.error(
          `Failed to reconstruct region from regCode during authentication: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }

    const sessionId = authRequest.SessionContext.SessionId;
    if (!regCode || !sessionId || !region) {
      this.logger.fatal(
        `Illegal invocation of UserAuthentication with inputs regCode:${regCode} sessionId:${sessionId} and region:${region?.endpoint}`
      );
      throw new WsError(
        WsErrorTypes.ERROR_TYPE_AUTHENTICATION,
        !regCode || !region
          ? WsErrorCodes.ERROR_INVALID_REG_CODE
          : WsErrorCodes.ERROR_INVALID_SESSION_ID,
        !regCode || !region
          ? ClientErrorCode.AuthNoRegCodeFound
          : ClientErrorCode.AuthNoSessionIdFound
      );
    }

    return await this.wsBrokerClient
      .authenticate(region, authRequest)
      .then((response) => {
        const data: Partial<SessionDetails> = {
          directoryId: response.AuthProviderInfo?.DirectoryId,
          domainName: response.AuthProviderInfo?.DomainName,
          sessionContext: response.SessionContext,
          nextAuthMethod: response?.NextAuthMethods?.[0],
          fallbackAuthMethod: response?.FallbackAuthMethod,
        };
        const authBlobs = this.authBlobFromAuthResponse(
          response.AuthProviderInfo?.AuthBlobs
        );
        if (authBlobs) {
          data.authBlob = authBlobs;
          this.logger.info('Received AuthBlob from Authenticate response');
        }
        if (!this.skipAuthCodeUpdate(authStep)) {
          data.authToken = response.AuthToken;
        }
        if (this.device.getIsOemHandshaked())
          rtcchannel?.brokerAuthenticateResult(true);
        this.sessionManager.set(data);
        this.metrics.emitMetricOperation(timedAuthMetric);
        return response;
      })
      .catch((error) => {
        this.logger.error('WSBrokerClient::Authenticate failed with ', error);
        if (this.device.getIsOemHandshaked())
          rtcchannel?.brokerAuthenticateResult(false);
        this.metrics.emitMetricOperation(timedAuthMetric, error);
        if (error instanceof WsError) {
          throw error;
        } else {
          throw new WsError(
            WsErrorTypes.ERROR_TYPE_AUTHENTICATION,
            WsErrorCodes.ERROR_CM_REQUEST_FAILED
          );
        }
      });
  }

  /**
   * Fetches authentication information for the given registration code and region
   * @param regCode - Registration code
   * @param region - Region information
   * @returns Authentication information response
   * @throws WsError if fetching auth info fails
   */
  async fetchAuthInfo(regCode?: string, region?: IRegion) {
    // Validate required inputs
    if (!regCode || !region) {
      this.logger.error('Attempted to fetch auth info without required inputs');
      throw new WsError(
        WsErrorTypes.ERROR_TYPE_REGISTRATION,
        WsErrorCodes.ERROR_INVALID_REG_CODE,
        ClientErrorCode.AuthNoRegCodeFound
      );
    }

    // Start metrics tracking
    const timedAuthInfoMetric = this.metrics.embark(Operation.GetAuthInfo);

    // Gather client information
    const clientVersion = this.device.getProductVersion() as string;
    const clientName = this.device.getBrokerClientName();
    const platformType = this.device.getPlatform() as PlatformType;
    const authCapabilities = this.device.getAuthCapabilities(region);
    // Generate client state ID for CSRF token validation
    const clientStateId = generateUUID();
    this.sessionManager.set({ clientStateID: clientStateId });

    // Generate PKCE codes for secure authentication
    const { codeVerifier, codeChallenge } =
      await CryptoUtils.generatePkceCodes();

    return await this.wsBrokerClient
      .fetchAuthInfo(
        regCode,
        RegionResolver.isConnectionAlias(regCode),
        region,
        clientVersion,
        clientName,
        platformType,
        authCapabilities,
        codeChallenge,
        clientStateId
      )
      .then((response) => {
        // Log successful retrieval
        this.logger.info(
          `Retrieved auth info for ${regCode} and sessionId ${response?.SessionContext?.SessionId} and provider ${response?.SessionContext?.AuthProvider}`
        );

        // Guard against a resolved-but-malformed response missing AuthMethods
        // to avoid an unhandled TypeError on the AuthMethods[0] reads below.
        // See https://sim.amazon.com/issues/AWS-WS-216797
        if (!response?.AuthMethods?.length) {
          this.logger.error(
            `GetAuthInfo returned no AuthMethods for ${regCode}; treating as service unavailable`
          );
          throw new WsError(
            WsErrorTypes.ERROR_TYPE_GETAUTHINFO,
            WsErrorCodes.ERROR_SERVICE_UNAVAILABLE,
            ClientErrorCode.AuthInfoServiceUnavailable
          );
        }

        // Handle OEM handshake if needed
        if (
          this.device.getIsOemHandshaked() &&
          response.AuthMethods[0]?.AuthProvider &&
          response.AuthMethods[0]?.Url
        ) {
          this.sendLoginURLWithAuthTypeToSolo(
            response.AuthMethods[0].AuthProvider,
            response.AuthMethods[0].Url
          );
        }

        // Update session information
        this.sessionManager.set({
          codeVerifier,
          sessionContext: response.SessionContext,
          nextAuthMethod: response.AuthMethods[0],
          primaryAuthMethod: response.AuthMethods[0],
          domainProperties: response.DomainProperties,
        });

        // Complete metrics and return response
        this.metrics.emitMetricOperation(timedAuthInfoMetric);
        return response;
      })
      .catch((error) => {
        // Handle errors with improved logging
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        this.logger.error(
          `Failed to retrieve auth info for ${regCode}: ${errorMessage}`
        );
        this.metrics.emitMetricOperation(timedAuthInfoMetric, error);

        // Rethrow WsError or wrap other errors with more context
        if (error instanceof WsError) {
          throw error;
        } else {
          throw new WsError(
            WsErrorTypes.ERROR_TYPE_GETAUTHINFO,
            WsErrorCodes.ERROR_UNKNOWN,
            ClientErrorCode.AuthInfoUnknown
          );
        }
      });
  }

  // processWarpDriveAuth method has been removed as it's no longer needed

  private skipAuthCodeUpdate(authStep?: IAuthRelativePath) {
    return authStep === 'reauthenticate';
  }

  private authBlobFromAuthResponse(
    authBlobs: Record<string, string> | undefined
  ): string | undefined {
    return authBlobs && Object.values(authBlobs)[0];
  }

  /**
   * Determines the WarpDrive login type based on URL pattern
   * @param url - The authentication URL
   * @returns The detected login type
   */
  private determineLoginType(url: string): WarpDriveLoginType {
    // Use the shared utility class for login type detection
    return LoginTypeDetector.detectLoginTypeFromUrl(url, this.logger);
  }

  private sendLoginURLWithAuthTypeToSolo(
    authProvider: AuthProvider,
    url: string
  ) {
    try {
      let loginUrl: string = '';
      let failedRedirectFallbackUrl: string = '';
      switch (authProvider) {
        case AuthProvider.WARP_DRIVE: {
          const warpDriveBuilder = new WarpDriveExternalNativeAuthUrlBuilder(
            url
          );
          warpDriveBuilder.setLocale();

          // Determine login type using helper method
          const loginType = this.determineLoginType(url);

          // Log the detected login type
          this.logger.info(
            `Building WarpDrive URL for login type: ${loginType}`
          );

          // Handle different login types with different CSRF validation approaches
          if (loginType === WarpDriveLoginType.WD_LOGIN) {
            // For WD login, set redirect URI and generate a new CSRF token
            warpDriveBuilder.setRedirectUri('warpdrive');
            warpDriveBuilder.setCsrfToken(null, true);
            this.logger.info('Using WD login flow with generated CSRF token');
          } else if (loginType === WarpDriveLoginType.UNIFIED_LOGIN) {
            // For Unified login, use client state ID for CSRF token
            const clientStateID = this.sessionManager.get('clientStateID');
            warpDriveBuilder.setRedirectUri('warpdrive');
            warpDriveBuilder.setCsrfToken(clientStateID, false);
            this.logger.info(
              'Using Unified login flow with client state ID as CSRF token'
            );
          } else if (loginType === WarpDriveLoginType.HARD_BLOCK) {
            this.logger.info(
              'Hard block login type detected - authentication blocked'
            );
          } else {
            this.logger.error('Invalid login type detected');
          }

          loginUrl = warpDriveBuilder.getUrl().href;

          // Set appropriate error code based on login type
          const clientErrorCode =
            loginType === WarpDriveLoginType.WD_LOGIN
              ? ClientErrorCode.AuthExternalLoginRedirectUnknownError
              : ClientErrorCode.AuthExternalUnififedRedirectUnknownError;

          failedRedirectFallbackUrl =
            this.buildExternalAuthRedirectFailedErrorHandlerUrl(
              clientErrorCode
            ).toString();
          break;
        }
        case AuthProvider.SAML_IAM: {
          const clientErrorCode =
            ClientErrorCode.AuthExternalSamlRedirectUnknownError;
          loginUrl = new URL(url).href;
          failedRedirectFallbackUrl =
            this.buildExternalAuthRedirectFailedErrorHandlerUrl(
              clientErrorCode
            ).toString();
          break;
        }
        case AuthProvider.IDC: {
          const idcBuilder = new ExternalAuthUrlBuilder(url);
          const clientErrorCode =
            ClientErrorCode.AuthExternalIdcRedirectUnknownError;
          loginUrl = idcBuilder.getUrl().href;
          failedRedirectFallbackUrl =
            this.buildExternalAuthRedirectFailedErrorHandlerUrl(
              clientErrorCode
            ).toString();
          break;
        }
        default: {
          this.logger.warn(
            `Unsupported auth provider for Solo Linux running in embedded-mode: ${authProvider}`
          );
        }
      }
      rtcchannel?.displayWarpDriveUrl(
        loginUrl,
        authProvider,
        failedRedirectFallbackUrl
      );
    } catch (error: any) {
      this.logger.error(
        `Error in sending Login URL and AuthProvider to Solo: ${JSON.stringify(
          error?.message
        )}`
      );
    }
  }

  private buildExternalAuthRedirectFailedErrorHandlerUrl(
    clientErrorCode: ClientErrorCode
  ): URL {
    const { hostname, port } = window.location;
    const url = new URL(`https://${hostname}${port ? `:${port}` : ''}/error`);

    url.searchParams.set(
      AppConstants.ERROR_URL_CLIENT_ERROR_CODE_SEARCH_PARAM_KEY,
      JSON.stringify(clientErrorCode)
    );
    url.searchParams.set(
      AppConstants.ERROR_URL_SOLO_ERROR_CODE_SEARCH_PARAM_KEY,
      ''
    );

    return url;
  }
}
